"use client";

import VoiceAgent, { type TutorAvatarHandle } from "@/components/VoiceAgent";
import type { Room } from "livekit-client";
import {
  ArrowUpToLine,
  Maximize,
  Maximize2,
  Mic,
  MicOff,
  Minimize,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { type ReactNode, useEffect, useLayoutEffect, useRef } from "react";
import styles from "./TutorStage.module.css";
import type { TutorView } from "./tutor-view";
import type { TutorStageState } from "./useTutorStage";

/**
 * The one avatar container of the desktop tutor views, its controls, and the
 * temporary fullscreen. Rendered inside the conversation area, which places
 * everything with CSS; the container itself never remounts across views.
 */
export function TutorAvatarStage({
  room,
  isActiveSession,
  stage,
  view,
  avatarAvailable,
  mountAvatar = true,
  isMuted,
  onToggleMute,
  onAvatarError,
  onAvatarHandle,
  fullscreenCaption,
}: {
  room: Room;
  isActiveSession: boolean;
  stage: TutorStageState;
  /** The view in effect: the header circle while narrower than the desktop views. */
  view: TutorView;
  avatarAvailable: boolean;
  /**
   * False while her shared session is still being created: the layout keeps her
   * place, but no viewer starts here (it would duplicate the shared one).
   */
  mountAvatar?: boolean;
  isMuted?: boolean;
  onToggleMute?: () => void;
  onAvatarError: (message: string | null) => void;
  onAvatarHandle: (handle: TutorAvatarHandle | null) => void;
  /** The current tutor turn, read beside her in fullscreen. */
  fullscreenCaption: ReactNode;
}) {
  const expandButtonRef = useRef<HTMLButtonElement>(null);
  const exitButtonRef = useRef<HTMLButtonElement>(null);
  const layerRef = useRef<HTMLDivElement>(null);
  const { fullscreenActive } = stage;
  const inConversation = view === "compact" || view === "large";

  // Under fullscreen the conversation stays mounted (scroll position, draft)
  // but inert: everything in the area except the fullscreen layer.
  useLayoutEffect(() => {
    const area = layerRef.current?.parentElement;
    if (!fullscreenActive || !area) return;
    const others = Array.from(area.children).filter(
      (child) => !child.hasAttribute("data-tutor-fullscreen")
    );
    for (const element of others) element.setAttribute("inert", "");
    return () => {
      for (const element of others) element.removeAttribute("inert");
    };
  }, [fullscreenActive]);

  // Fullscreen takes focus on its exit control.
  useEffect(() => {
    if (fullscreenActive) exitButtonRef.current?.focus();
  }, [fullscreenActive]);

  if (!isActiveSession) return null;

  return (
    <>
      {mountAvatar ? (
        <VoiceAgent
          room={room}
          isRecording={isActiveSession}
          compact
          requireVideoTrack
          renderAudioRenderer={false}
          presentation="external"
          inlineAvatarFrameClassName={styles.avatarFrame}
          externalBackground={view === "header" && !fullscreenActive}
          renderingHidden={stage.renderingHidden}
          onAvatarError={onAvatarError}
          onAvatarHandle={onAvatarHandle}
        />
      ) : null}

      {view === "header" && avatarAvailable && mountAvatar ? (
        // Today's circle: hover shows the expand icon, click opens fullscreen.
        <button
          type="button"
          className={styles.thumbButton}
          inert={fullscreenActive}
          aria-label="Show avatar fullscreen"
          title="Fullscreen"
          onClick={(event) => stage.enterFullscreen(event.currentTarget)}
        >
          <Maximize2 size={22} aria-hidden="true" />
        </button>
      ) : null}

      {inConversation && avatarAvailable && mountAvatar ? (
        <>
          {/* Her head and neck: hovering reveals her controls; double-click is a
              mouse shortcut to fullscreen (the buttons stay for keyboard and touch). */}
          <div
            className={styles.tutorHover}
            aria-hidden="true"
            onDoubleClick={() => stage.enterFullscreen(expandButtonRef.current)}
          />
          <div
            className={styles.avatarTools}
            role="group"
            aria-label="Avatar controls"
            inert={fullscreenActive}
          >
            <button
              type="button"
              className={styles.avatarTool}
              aria-label={view === "large" ? "Make avatar smaller" : "Make avatar larger"}
              title={view === "large" ? "Make smaller" : "Make larger"}
              onClick={() => stage.changeView(view === "large" ? "compact" : "large")}
            >
              {view === "large" ? (
                <ZoomOut size={18} aria-hidden="true" />
              ) : (
                <ZoomIn size={18} aria-hidden="true" />
              )}
            </button>
            <button
              type="button"
              className={styles.avatarTool}
              aria-label="Move avatar to the top bar"
              title="Move to top bar"
              onClick={() => stage.changeView("header")}
            >
              <ArrowUpToLine size={18} aria-hidden="true" />
            </button>
            <button
              ref={expandButtonRef}
              type="button"
              className={styles.avatarTool}
              aria-label="Show avatar fullscreen"
              title="Fullscreen (or double-click the avatar)"
              onClick={(event) => stage.enterFullscreen(event.currentTarget)}
            >
              <Maximize size={18} aria-hidden="true" />
            </button>
          </div>
        </>
      ) : null}

      {fullscreenActive ? (
        <>
          <div className={styles.fullscreenBackdrop} aria-hidden="true" data-tutor-fullscreen />
          <div
            ref={layerRef}
            data-tutor-fullscreen
            className={styles.fullscreenLayer}
            role="dialog"
            aria-modal="true"
            aria-label="Avatar fullscreen"
          >
            <div
              className={styles.fullscreenHit}
              aria-hidden="true"
              onDoubleClick={stage.exitFullscreen}
            />
            <div className={styles.fullscreenControls}>
              {onToggleMute ? (
                <button
                  type="button"
                  className={styles.stageIconButton}
                  aria-label={isMuted ? "Unmute microphone" : "Mute microphone"}
                  title={isMuted ? "Unmute microphone" : "Mute microphone"}
                  onClick={onToggleMute}
                >
                  {isMuted ? (
                    <MicOff size={18} aria-hidden="true" />
                  ) : (
                    <Mic size={18} aria-hidden="true" />
                  )}
                </button>
              ) : null}
              <button
                ref={exitButtonRef}
                type="button"
                className={styles.stageIconButton}
                aria-label="Exit fullscreen"
                title="Exit fullscreen (Esc, or double-click the avatar)"
                onClick={stage.exitFullscreen}
              >
                <Minimize size={18} aria-hidden="true" />
              </button>
            </div>
            <div className={styles.fullscreenCaption}>{fullscreenCaption}</div>
          </div>
        </>
      ) : null}
    </>
  );
}

/**
 * Latest message: three invisible floats, aligned with her, keep the large turn
 * off her face; placed first in the transcript content.
 */
export function TutorWrapFloats() {
  return (
    <span className={styles.wrapFloats} aria-hidden="true">
      <span className={styles.wrapSpacer} />
      <span className={styles.wrapShape} />
      <span className={styles.wrapBelow} />
    </span>
  );
}
